import { useEffect, useState, useRef } from "react";
import { Calendar, Send, User, Building2, Phone, Mail, MessageSquare } from "lucide-react";

// CALENDLY URL - Replace with your actual Calendly link when ready
const CALENDLY_URL = "https://calendly.com/partner-experienceinterlude"; // TODO: Update with actual Calendly URL

const PartnershipSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const subject = encodeURIComponent(`Partnership Inquiry from ${formData.company}`);
    const body = encodeURIComponent(
`New Partnership Inquiry

Name: ${formData.name}
Company: ${formData.company}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}

Message:
${formData.message}

---
Sent from INTERLUDE website`
    );

    window.location.href = `mailto:partner@experienceinterlude.com?subject=${subject}&body=${body}`;

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      }, 3000);
    }, 500);
  };

  const handleCalendlyClick = () => {
    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="partnership" className="partnership-section" ref={sectionRef} data-testid="partnership-section">
      <div className={`partnership-content ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
        <span className="partnership-label">Let's Create Together</span>
        <h2 className="partnership-title">Elevate Your Space</h2>
        <p className="partnership-text">
          We work closely with hotels, restaurants, cafés, and hospitality brands to develop 
          signature scents that embody your identity and enhance guest experiences.
        </p>

        <div className="partnership-features">
          <div className="feature-item">
            <span className="feature-number">01</span>
            <h4 className="feature-title">Consultation</h4>
            <p className="feature-text">Understanding your brand, space, and vision</p>
          </div>
          <div className="feature-item">
            <span className="feature-number">02</span>
            <h4 className="feature-title">Creation</h4>
            <p className="feature-text">Bespoke fragrance development & sampling</p>
          </div>
          <div className="feature-item">
            <span className="feature-number">03</span>
            <h4 className="feature-title">Implementation</h4>
            <p className="feature-text">Seamless integration into your environment</p>
          </div>
        </div>

        {/* Calendly Booking Button */}
        <div className="calendly-cta">
          <button
            onClick={handleCalendlyClick}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="calendly-btn"
          >
            <Calendar size={16} />
            Book a Scent Consultation
          </button>
          <p className="calendly-note">Schedule a 30-minute discovery call</p>
        </div>
      </div>

      {/* Contact Form */}
      <div className={`contact-form-container ${isVisible ? 'animate-fadeInUp delay-300' : 'opacity-0'}`}>
        <div className="contact-form-header">
          <h3 className="contact-form-title">Send Us an Inquiry</h3>
          <p className="contact-form-subtitle">Tell us about your space and vision</p>
        </div>

        {submitted ? (
          <div className="form-success" data-testid="form-success">
            <div className="success-icon">&#10003;</div>
            <p>Your email client should open shortly.</p>
            <p className="success-note">Thank you for your interest!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  <User size={14} />
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Smith"
                  className="form-input"
                  data-testid="input-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="company" className="form-label">
                  <Building2 size={14} />
                  Company / Venue *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  placeholder="The Grand Hotel"
                  className="form-input"
                  data-testid="input-company"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  <Mail size={14} />
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@grandhotel.com"
                  className="form-input"
                  data-testid="input-email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  <Phone size={14} />
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                  className="form-input"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label htmlFor="message" className="form-label">
                <MessageSquare size={14} />
                Tell Us About Your Vision *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Describe your space, brand, and what kind of scent experience you're looking to create..."
                className="form-textarea"
                rows={4}
                data-testid="input-message"
              />
            </div>

            <button
              type="submit"
              className="btn-primary form-submit"
              disabled={isSubmitting}
              data-testid="form-submit-btn"
            >
              {isSubmitting ? (
                'Opening Email...'
              ) : (
                <>
                  <Send size={16} />
                  Send Inquiry
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default PartnershipSection;
